import { Search } from "lucide-react";

import { Button } from "@/common/ui/button";
import { focusOutline } from "@/common/ui/focus-outline";
import { Input } from "@/common/ui/input";

import type { AuditAction } from "./conversation-logs-types";
import type { ConversationLogsFilters } from "./use-conversation-logs";

interface LogsToolbarProps {
  filters: ConversationLogsFilters;
  onFiltersChange: (filters: ConversationLogsFilters) => void;
  onSearch: () => void;
  isLoading: boolean;
}

const ACTION_OPTIONS: { label: string; value: AuditAction | "" }[] = [
  { label: "All Actions", value: "" },
  { label: "Query", value: "query" },
  { label: "Tool Call", value: "tool_call" },
  { label: "Prefill Generate", value: "prefill_generate" },
];

export function LogsToolbar({
  filters,
  onFiltersChange,
  onSearch,
  isLoading,
}: LogsToolbarProps): React.ReactElement {
  function updateFilter<K extends keyof ConversationLogsFilters>(
    key: K,
    value: ConversationLogsFilters[K]
  ): void {
    onFiltersChange({ ...filters, [key]: value });
  }

  return (
    <div data-testid="logs-toolbar" className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="start-time" className="text-xs font-medium text-muted-foreground">
            Start
          </label>
          <Input
            id="start-time"
            data-testid="filter-start-time"
            type="datetime-local"
            value={filters.startTime}
            onChange={(e) => updateFilter("startTime", e.target.value)}
            className="w-52"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="end-time" className="text-xs font-medium text-muted-foreground">
            End
          </label>
          <Input
            id="end-time"
            data-testid="filter-end-time"
            type="datetime-local"
            value={filters.endTime}
            onChange={(e) => updateFilter("endTime", e.target.value)}
            className="w-52"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="user-id" className="text-xs font-medium text-muted-foreground">
            User ID
          </label>
          <Input
            id="user-id"
            data-testid="filter-user-id"
            type="text"
            placeholder="Filter by user ID"
            value={filters.userId}
            onChange={(e) => updateFilter("userId", e.target.value)}
            className="w-48"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="session-id" className="text-xs font-medium text-muted-foreground">
            Session ID
          </label>
          <Input
            id="session-id"
            data-testid="filter-session-id"
            type="text"
            placeholder="Filter by session"
            value={filters.sessionId}
            onChange={(e) => updateFilter("sessionId", e.target.value)}
            className="w-48"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="action-filter" className="text-xs font-medium text-muted-foreground">
            Action
          </label>
          <select
            id="action-filter"
            data-testid="filter-action"
            value={filters.action}
            onChange={(e) => updateFilter("action", e.target.value as AuditAction | "")}
            className={`flex h-9 w-40 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors ${focusOutline}`}
          >
            {ACTION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <Button data-testid="search-logs-button" size="sm" onClick={onSearch} disabled={isLoading}>
          <Search className="mr-2 h-4 w-4" />
          Search
        </Button>
      </div>
    </div>
  );
}
